import "./table.scss";

import { Fragment, ReactNode, useId } from "react";
import cn from "clsx";
import { slugify } from "design-system/utils/slugify";
import { Alignment } from "design-system/types/types";
import { Row, TableRow } from "./table-row";
import { TableColumnHeader } from "./table-column-header";
import {
  ComponentHeader,
  ComponentHeaderProps,
} from "design-system/components/primitives/component-header/component-header";

export interface RowGroup {
  groupHeader?: string;
  rows: Array<Row>;
}

export interface ColumnHeader {
  data: string;
  isBold?: boolean;
  hrRule?: boolean;
}

export interface TableProps {
  align?: Alignment;
  title?: ReactNode;
  componentHeader?: ComponentHeaderProps;
  columnGroupHeaders?: Array<string>;
  columnHeaders?: Array<ColumnHeader>;
  rows?: Array<RowGroup>;
  footerText?: ReactNode;
  HeadingLevel?: "h2" | "h3";
  TitleHeadingLevel?: "h2" | "h3" | "h4";
}

/**
 * For displaying tabular data.
 *
 * ## How it works:
 * If `columnGroupHeaders` are provided in the data, data will be grouped by column.
 * If rows are nested within rows, subrows are supported.
 *
 * ## See it in use on...
 * - The [kitchen sink detail page](/story/example-pages-detail-pages-kitchen-sink--story)
 *
 *
 * - **`id: CB-017-000-00`**
 * - **`data-region: cb__table`**
 */

export function Table({
  align = "wide",
  columnGroupHeaders,
  columnHeaders = [],
  rows = [],
  title,
  footerText,
  componentHeader,
  HeadingLevel = "h2",
  TitleHeadingLevel = "h3",
}: TableProps) {
  const tableId = useId().replace(/:/g, "").toLowerCase();

  const hasColumnGroupHeaders =
    columnGroupHeaders && columnGroupHeaders.length > 0;

  const hasColumnHeaders = columnHeaders.some(
    (header) => header.data?.trim() !== "",
  );

  const hasRowHeaders = rows.some((rowGroup) =>
    rowGroup.rows.some((row) => row.rowHeader),
  );

  //rowHeaders (if exists) do not count as columns
  const totalColumns = hasRowHeaders
    ? columnHeaders.length - 1
    : columnHeaders.length;

  const wrapperClass = cn(`hbs-global-align-${align}`, "hbs-component--table");

  return (
    <div className={wrapperClass} data-region="cb__table">
      {componentHeader && (
        <ComponentHeader {...componentHeader} HeadingLevel={HeadingLevel} />
      )}

      {title && (
        <TitleHeadingLevel className="hbs-table-title hbs-text-h4">
          {title}
        </TitleHeadingLevel>
      )}

      <table
        id={tableId}
        className={cn(
          "hbs-table",
          footerText ? "hbs-table--has-footer" : null,
          totalColumns < 4 && !hasColumnGroupHeaders
            ? "hbs-table--no-wide-columns"
            : null,
        )}
      >
        {(hasColumnGroupHeaders || hasColumnHeaders) && (
          <thead>
            {hasColumnGroupHeaders && (
              <tr className="hbs-table-column-group-headers">
                {hasRowHeaders ? <td rowSpan={2}></td> : null}
                {columnGroupHeaders.map((header, i) => (
                  <th key={i} colSpan={totalColumns} scope="col">
                    {header}
                  </th>
                ))}
              </tr>
            )}

            {hasColumnGroupHeaders ? (
              // If there are column group headers, display column headers for each column group
              <tr className="hbs-table-column-headers--with-group-headers">
                {columnGroupHeaders.map((groupHeader, groupHeaderIndex) => (
                  <Fragment key={groupHeaderIndex}>
                    {columnHeaders.map((columnHeader, i) => {
                      if (!columnHeader.data) {
                        return null;
                      }
                      return (
                        <TableColumnHeader
                          key={i}
                          tableId={tableId}
                          columnHeader={columnHeader.data}
                          groupHeader={slugify(groupHeader)}
                          index={i}
                          hrRule={columnHeader.hrRule}
                        />
                      );
                    })}
                  </Fragment>
                ))}
              </tr>
            ) : (
              // Otherwise just display one set of column headers.
              <tr>
                {columnHeaders.map((columnHeader, i) => {
                  if (!columnHeader.data) {
                    return (
                      <td
                        key={i}
                        className={
                          columnHeader.hrRule ? "hbs-table-cell-hr-rule" : ""
                        }
                      ></td>
                    );
                  }

                  return (
                    <TableColumnHeader
                      key={i}
                      index={i}
                      tableId={tableId}
                      columnHeader={columnHeader.data}
                      hrRule={columnHeader.hrRule}
                    />
                  );
                })}
              </tr>
            )}
          </thead>
        )}

        <tbody>
          {rows.map((rowGroup, rowGroupIndex) => {
            const rowGroupHeader = slugify(rowGroup.groupHeader);

            return (
              <Fragment key={rowGroupIndex}>
                {rowGroupHeader && (
                  <tr className="hbs-table-rowgroup-header">
                    <th id={rowGroupHeader}>{rowGroup.groupHeader}</th>
                    <td
                      className={cn(
                        "empty-cell",
                        columnGroupHeaders ? "has-colgroups" : "",
                      )}
                      colSpan={hasRowHeaders ? totalColumns : totalColumns - 1}
                    ></td>
                  </tr>
                )}

                {rowGroup.rows.map((rowGroupRow, rowIndex) => (
                  <Fragment key={rowIndex}>
                    <TableRow
                      row={rowGroupRow}
                      columnHeaders={columnHeaders}
                      isNewGroup={!rowGroupHeader && rowIndex === 0}
                    />

                    {rowGroupRow.rows?.map((row, i) => (
                      <TableRow
                        key={i}
                        row={row}
                        columnHeaders={columnHeaders}
                        isSubRow={true}
                      />
                    ))}
                  </Fragment>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {footerText && <div className="hbs-table-footer-text">{footerText}</div>}
    </div>
  );
}
